import React, { Fragment, useState } from 'react';
import { Button, Input, Label, Row, Progress } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isEmpty, groupBy } from 'lodash-es';

import ConfirmModal from '../../../utilities/form-components/ConfirmModal';
import InlineAlert from '../../../utilities/form-components/InlineAlert';
import Step4File from './Step4File';
import imageUploadErrors from './imageUploadErrors';
import ImageSizeTestBanner from '../ImageSizeTestBanner';

/**
 * Presentational list beneath the dropzone: overall progress, error summary/banners, and the file
 * tiles. All file state lives in the parent's reducer — this component only renders props and owns
 * its own transient UI (the delete-errors modal and the small-image warning banner). Replaces the
 * old redux-connected Step4FileContainer, which mirrored props.files into local state (a redundant
 * second source of truth and part of the COR-3192 drift).
 *
 * All entries in state render a tile; the drop handler's batchSize=20 loop controls how many
 * files are added at a time, throttling memory use (thumbnails) on large drops.
 */
const Step4FileList = ({
  entries,
  numFilesTotal,
  numFilesComplete,
  catalogID,
  houseID,
  type,
  fileUploadFormat,
  filterOnlyErrors,
  lotNumbers,
  inventoryNumbers,
  handleErrorFilter,
  deleteFilesWithErrors,
  onComplete,
  onError,
  onAbort,
  onDelete,
  onUpdate,
  onClearUpdated
}) => {
  const [showModal, setShowModal] = useState(false);
  const [showWarningBanner, setShowWarningBanner] = useState(false);

  const toggleDeleteImageModal = () => setShowModal(prev => !prev);
  const handleImageSizeCheck = isSmall => {
    if (isSmall) {
      setShowWarningBanner(true);
    }
  };

  if (isEmpty(entries)) {
    return null;
  }

  const overallProgress = Math.round((numFilesComplete / numFilesTotal) * 100);

  const fileErrors = entries.filter(entry => entry.hasError);
  const numFilesErrors = fileErrors.length;

  const visibleEntries = entries.filter(entry => entry.admitted || entry.isComplete);

  let errorSummary = null;
  let errorGroups = null;
  if (numFilesErrors > 0) {
    const baseText =
      'The following images have errors. Please note that any images with errors will not be saved:';
    const secondaryText = fileErrors.map(entry => entry.name).join(', ');

    errorSummary = (
      <InlineAlert
        showPhones={false}
        titleText={`Error Summary: Success: ${numFilesComplete -
          numFilesErrors}, Error: ${numFilesErrors}, Pending: ${numFilesTotal -
          numFilesComplete}, Total: ${numFilesTotal}`}
        baseText={baseText}
        secondaryText={secondaryText}
      />
    );

    errorGroups = groupBy(fileErrors, entry => entry.error.code);
  }

  return (
    <div className="mt-4">
      <p className="mb-2">
        Overall Upload Progress: {overallProgress}%{' '}
        ({numFilesComplete} of {numFilesTotal} uploaded
        {numFilesErrors > 0 && `, ${numFilesErrors} error${numFilesErrors !== 1 ? 's' : ''}`})
      </p>
      <Progress className="mb-2 w-50" value={overallProgress} />

      {errorSummary}

      {errorGroups &&
        Object.keys(errorGroups).map(errorCode => {
          const { title, summary } = imageUploadErrors(errorCode, type);
          const secondaryText = errorGroups[errorCode].map(entry => entry.name).join(', ');

          return (
            <InlineAlert
              key={errorCode}
              showPhones={false}
              titleText={title}
              baseText={summary}
              secondaryText={secondaryText}
            />
          );
        })}

      <div>
        {numFilesErrors > 0 && (
          <Fragment>
            <Input
              id="viewFileErrors"
              className="ml-0 mr-1 position-relative cursor-pointer"
              checked={filterOnlyErrors}
              onChange={handleErrorFilter}
              type="checkbox"
            />
            <Label for="viewFileErrors" className="mr-4 mt-0 mb-3">
              View items with errors
            </Label>
          </Fragment>
        )}

        {numFilesErrors > 0 && (
          <Button color="link" onClick={toggleDeleteImageModal}>
            <FontAwesomeIcon className="color-blue cursor-pointer mr-1" icon="trash-alt" />
            Delete items with errors
          </Button>
        )}
      </div>

      <ConfirmModal
        actionFunction={deleteFilesWithErrors}
        bodyText="Are you sure you want to delete all files with errors?"
        buttonText="Delete"
        showModal={showModal}
        title="Delete Items"
        toggleFunction={toggleDeleteImageModal}
      />

      {showWarningBanner && (
        <Row>
          <ImageSizeTestBanner />
        </Row>
      )}

      <Row>
        {visibleEntries.map(entry => (
            <Step4File
              key={entry.id}
              entry={entry}
              catalogID={catalogID}
              houseID={houseID}
              fileUploadFormat={fileUploadFormat}
              filterOnlyErrors={filterOnlyErrors}
              lotNumbers={lotNumbers}
              inventoryNumbers={inventoryNumbers}
              handleImageSizeCheck={handleImageSizeCheck}
              onComplete={onComplete}
              onError={onError}
              onAbort={onAbort}
              onDelete={onDelete}
              onUpdate={onUpdate}
              onClearUpdated={onClearUpdated}
            />
          ))}
      </Row>
    </div>
  );
};

export default Step4FileList;
